import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { changePasswordSchema } from 'utils/form-validation-schema';
import { changePassword } from 'services/UserAccountService';
import { InputControlled } from 'components/UI/InputControlled/InputControlled';
import { Checkbox } from 'components/UI/Checkbox/Checkbox';
import { Callout } from 'components/UI/Callout/Callout';
import { ReactComponent as HelpIcon } from 'assets/icons/svgs/icon-help.svg';
import useWindowDimensions from 'hooks/use-window-dimensions';
import './ChangePassword.scss';

export const ChangePassword = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [newPasswordInputType, setNewPasswordInputType] = useState('password');
  const [showFormIncompleteError, setShowFormIncompleteError] = useState(false);
  const [hideErrorForCurrentPassword, setHideErrorForCurrentPassword] = useState(true);
  const [openPopup, setOpenPopup] = useState(false);
  const { isMobile } = useWindowDimensions();
  const popupHintClass = isMobile ? 'hint-visible-m' : 'hint-visible-d';

  const showPopupHandler = (inputId) => {
    if (!isMobile) {
      setOpenPopup(inputId);
    }
  };

  const showPopupMobileHandler = (inputId) => {
    setOpenPopup((prevState) => (prevState ? false : inputId));
  };

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(changePasswordSchema),
  });

  const {
    formState: { isValid },
  } = methods;

  const showPasswordChangeHandler = (event) => {
    const checkboxIsChecked = event.target.checked;
    const type = checkboxIsChecked ? 'text' : 'password';
    setNewPasswordInputType(type);
  };

  const submitButtonClickHandler = () => {
    setShowFormIncompleteError(true);
    setHideErrorForCurrentPassword(false);
  };

  const changePasswordFormSubmitHandler = async (formData) => {
    setIsProcessing(true);
    setShowFormIncompleteError(true);

    const response = await changePassword(formData);

    setSuccessMessage(response?.SuccessMessage);
    setErrorMessage(response?.ErrorMessage);

    setIsProcessing(false);
  };

  return (
    <section className="container">
      <div className="row">
        <div className="col-md-8 col-md-offset-1">
          <h2>Change password</h2>
          <p className="large form-intro" data-testid="form-intro">
            To change your account password, please enter your current password, followed by your
            new password.
          </p>
        </div>
      </div>

      <FormProvider {...methods}>
        <form
          name="passwordForm"
          className="change-password-form"
          noValidate="novalidate"
          onSubmit={methods.handleSubmit(changePasswordFormSubmitHandler)}
        >
          <div className="row">
            <div className="col-sm-6 col-md-4 col-md-offset-1">
              <div className="form-section">
                <div className="form-group">
                  <InputControlled
                    type="password"
                    id="currentPassword"
                    labelText="Current password"
                    hideErrorMsgForRequired={hideErrorForCurrentPassword}
                  />
                </div>

                <div className="form-group">
                  <div className="input-container has-hint">
                    <InputControlled
                      type={newPasswordInputType}
                      id="newPassword"
                      labelText="New password"
                      showPopupHandler={showPopupHandler}
                    />

                    <button
                      type="button"
                      className="hint-btn visible-xs"
                      onClick={() => showPopupMobileHandler('newPassword')}
                    >
                      <HelpIcon className="registration-form__help-icon" />
                    </button>
                    <div
                      className={`form-hint ${openPopup === 'newPassword' ? popupHintClass : ''}`}
                    >
                      <p>
                        Password should be <strong>6 or more characters</strong> and contain:
                      </p>
                      <p>
                        - English upper OR lower case letters (A to Z); <strong>and</strong>
                      </p>
                      <p>- At least one number (0 to 9) or character (such as !, $, #, %)</p>
                      <p>
                        <strong>But please don't:</strong>
                      </p>
                      <p>- Use your account login details or name in the password</p>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-container">
                    <Checkbox
                      checkboxChangeHandler={showPasswordChangeHandler}
                      label="Show password"
                      muiProps={{ id: 'showPassword' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-8 col-md-offset-1">
              {!isValid && showFormIncompleteError && (
                <Callout errorMessage="Please complete change password form." />
              )}

              {errorMessage && <Callout errorMessage={errorMessage} />}
              {successMessage && <Callout successMessage={successMessage} />}

              <button
                className="btn btn-primary"
                type="submit"
                disabled={isProcessing}
                onClick={submitButtonClickHandler}
                data-testid="save-btn"
              >
                <span>{isProcessing ? 'Processing...' : 'Save'} </span>
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </section>
  );
};
